import React, { useState } from 'react';
import { Col, FormControl, Row } from 'react-bootstrap';
import CustomMultiSelect from '../common/CustomMultiSelect';
import { Plus } from 'react-bootstrap-icons';
import RealEstateIRR from '../calculators/Irr';
import PinuiBinuiIRR from '../calculators/PinuiBinui';
import PaperApartmentIRR from '../calculators/PaperApartment';
import CustomerSelectModal from '../calculators/CustomerSelectModal';
import { apartmentTypes } from '../../helpers/global';
import { APARTMENT_STATUS, APARTMENT_STATUS_LABELS } from '../../helpers/constants';
import useLocalization from '../../hooks/useLocalization';
import cities from '../../helpers/israel-cities.json';
import CustomerSelector from './CustomerSelector';
import AlertModal from '../common/AlertModal';
import AddEditFieldInfo from './AddEditFieldInfo';
import { apartmentServices } from '../../services/appartmentServices';
import { toast } from 'react-toastify';
import UnderlineButton from '../common/UnderlineButton';

const AddEditApartmentForm = ({
  editMode,
  apartmentType,
  apartmentValues,
  setApartment,
  adminCompTable,
  apartmentExtraInfo,
  setApartmentExtraInfo,
  customerPickerModalMeta,
  setCustomerPickerModalMeta,
  handleSelectCustomer,
  assigneUsers,
  handleApartmentAfterNoteUpdate,
  setCsvDataFields,
  saveApartment
}) => {
  const { translate, isRTL } = useLocalization();
  const [fieldInfoModalMeta, setFieldInfoModalMeta] = useState(null);
  const [alertModalMeta, setAlertModalMeta] = useState(null);

  const checkIfCustomerHasProfile = selectedCustomer => {
    return selectedCustomer?.apartmentProfiles?.length > 0;
  };

  const handleCustomerSelect = (selectedCustomer, profile = null) => {
    if (checkIfCustomerHasProfile(selectedCustomer)) {
      onCustomerSelect(selectedCustomer, profile);
    } else {
      setAlertModalMeta({
        customerProileShow: true,
        alertText: 'This customer has no profiles.Please select another one!'
      });
    }
  };

  const onCustomerSelect = (selectedCustomer, profile = null) => {
    let previousSelcectedCustomers = customerPickerModalMeta?.selectedCustomer || [];

    if (previousSelcectedCustomers?.find(c => c?.customer?._id === selectedCustomer?._id)) {
      previousSelcectedCustomers = previousSelcectedCustomers?.filter(c => c?.customer?._id !== selectedCustomer?._id);
    } else {
      previousSelcectedCustomers = [
        ...previousSelcectedCustomers,
        {
          customer: selectedCustomer,
          status: 'Assigned',
          profile: profile || selectedCustomer?.apartmentProfiles[0]?._id,
          assignmentDate: new Date()
        }
      ];
    }
    setCustomerPickerModalMeta(meta => ({ ...meta, selectedCustomer: previousSelcectedCustomers }));
  };

  const removeCustomer = customer => {
    setApartmentExtraInfo({
      ...apartmentExtraInfo,
      profileAssignments: apartmentExtraInfo?.profileAssignments?.filter(pa => pa?.customer?._id !== customer?._id)
    });
  };

  const updateApartmentNotes = async note => {
    if (!editMode) {
      setFieldInfoModalMeta(meta => (meta ? { ...meta, note, progress: true } : meta));

      await saveApartment(false, {
        notes: [...(apartmentExtraInfo?.notes || []), { fieldKey: fieldInfoModalMeta?.fieldKey, note }]
      });
      setFieldInfoModalMeta(null);
      return;
    }
    setFieldInfoModalMeta(meta => (meta ? { ...meta, note, progress: true } : meta));
    const requestBody = {
      fieldKey: fieldInfoModalMeta?.fieldKey,
      note
    };
    const { response, error } = await apartmentServices.updateApartmentNotes(apartmentExtraInfo?._id, requestBody);
    if (error) {
      setFieldInfoModalMeta(meta => (meta ? { ...meta, progress: false } : meta));
      toast.error(error.message);
      return;
    }

    toast.success('Note updated successfully');
    setFieldInfoModalMeta(null);
    setApartmentExtraInfo({
      ...apartmentExtraInfo,
      notes: response.notes
    });
    handleApartmentAfterNoteUpdate(apartmentExtraInfo?._id, response.notes);
  };

  return (
    <div>
      <div className="border rounded mx-3 my-2 p-2 calculator-header">
        <Row className="m-0 align-items-end">
          <Col xs={12} sm={6} md={2} className="p-1 ">
            <h6 className="smallFont">{translate('apartment_type')}</h6>
            <CustomMultiSelect
              selectedItems={apartmentExtraInfo?.type}
              items={apartmentTypes}
              onChange={type => setApartmentExtraInfo({ ...apartmentExtraInfo, type })}
              isMulti={false}
              closeMenuOnSelect
              height="30px"
            />
            <h6 className="smallFont mt-2">{translate('gush_tat_helka')}</h6>
            <FormControl
              size="sm"
              value={apartmentExtraInfo?.gushTatHelka}
              onChange={e => setApartmentExtraInfo({ ...apartmentExtraInfo, gushTatHelka: e.target.value })}
            />
          </Col>
          <Col xs={12} sm={6} md={2} className="p-1">
            <h6 className="smallFont">{translate('status')}</h6>
            <CustomMultiSelect
              selectedItems={apartmentExtraInfo?.status}
              items={APARTMENT_STATUS?.map(s => ({
                label: translate(s),
                value: APARTMENT_STATUS_LABELS[s],
                isDisabled: s === 'preparing_contract' || s === 'bought_by_our_client'
              }))}
              onChange={status => setApartmentExtraInfo({ ...apartmentExtraInfo, status })}
              isMulti={false}
              closeMenuOnSelect
              placeholder=""
              height="30px"
            />
          </Col>{' '}
          <Col xs={12} sm={6} md={8} className={`px-2 p-1 d-flex flex-column gap-1 border-${isRTL ? 'end' : 'start'}`}>
            <div className="d-flex align-items-center justify-content-start">
              <h6 className="smallFont">{translate('related_customers')}</h6>

              <UnderlineButton
                Icon={Plus}
                text={translate('select_customer')}
                className="text-primary-new mb-2 mx-2"
                onClick={() => {
                  const status = apartmentExtraInfo?.status;
                  const canAssign =
                    status === APARTMENT_STATUS_LABELS.available || status === APARTMENT_STATUS_LABELS.hot;
                  if (!canAssign) {
                    toast.info(
                      'Apartment cannot be assigned in its current status. Please ensure the apartment status is set to Available or Hot.'
                    );
                    return;
                  }
                  setCustomerPickerModalMeta({ selectedCustomer: apartmentExtraInfo?.profileAssignments || [] });
                }}
              />
            </div>

            <div className="d-flex align-items-end gap-1 flex-wrap">
              {apartmentExtraInfo?.profileAssignments?.length > 0 ? (
                <CustomerSelector
                  apartmentExtraInfo={apartmentExtraInfo}
                  onRemoveCustomer={removeCustomer}
                  setApartmentExtraInfo={setApartmentExtraInfo}
                />
              ) : (
                <div className="text-muted">{translate('no_customers_selected')}</div>
              )}
            </div>
          </Col>
        </Row>
      </div>
      {apartmentType === apartmentTypes[0]?.value && (
        <RealEstateIRR
          customer={apartmentValues}
          addApartmentMode
          adminCompTable={adminCompTable}
          onChange={setApartment}
          assigneUsers={assigneUsers}
          onFieldInfoClick={setFieldInfoModalMeta}
          allowNote
          notes={apartmentExtraInfo?.notes}
          setCsvDataFields={setCsvDataFields}
        />
      )}{' '}
      {apartmentType === apartmentTypes[1]?.value && (
        <PinuiBinuiIRR
          addApartmentMode
          customer={apartmentValues}
          adminCompTable={adminCompTable}
          onChange={setApartment}
          assigneUsers={assigneUsers}
          onFieldInfoClick={setFieldInfoModalMeta}
          allowNote
          notes={apartmentExtraInfo?.notes}
          setCsvDataFields={setCsvDataFields}
        />
      )}
      {apartmentType === apartmentTypes[2]?.value && (
        <PaperApartmentIRR
          addApartmentMode
          customer={apartmentValues}
          adminCompTable={adminCompTable}
          onChange={setApartment}
          assigneUsers={assigneUsers}
          onFieldInfoClick={setFieldInfoModalMeta}
          allowNote
          notes={apartmentExtraInfo?.notes}
          setCsvDataFields={setCsvDataFields}
        />
      )}
      <AddEditFieldInfo
        show={Boolean(fieldInfoModalMeta)}
        onHide={() => setFieldInfoModalMeta(null)}
        value={
          fieldInfoModalMeta?.note ??
          apartmentExtraInfo?.notes?.find(n => n?.fieldKey === fieldInfoModalMeta?.fieldKey)?.note
        }
        onSave={updateApartmentNotes}
        label={fieldInfoModalMeta?.label}
        onChange={note => setFieldInfoModalMeta(meta => (meta ? { ...meta, note } : meta))}
        fieldKey={fieldInfoModalMeta?.fieldKey}
        progress={fieldInfoModalMeta?.progress}
      />
      <CustomerSelectModal
        show={Boolean(customerPickerModalMeta)}
        onHide={() => setCustomerPickerModalMeta(null)}
        selectedCustomer={customerPickerModalMeta?.selectedCustomer?.map(c => ({
          ...c.customer,
          selectedProfile: c.profile
        }))}
        onCustomerSelect={handleCustomerSelect}
        onSubmit={handleSelectCustomer}
        multiSelect
        submitButtonText="Select"
        headerText="Select customer"
        showProfiles={true}
      />
      <AlertModal
        show={alertModalMeta?.customerProileShow}
        alertText={alertModalMeta?.alertText}
        onHide={() => setAlertModalMeta(null)}
        onContinueClick={() => setAlertModalMeta(null)}
        continueButtonText="OK"
      />
    </div>
  );
};

export default AddEditApartmentForm;
